import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ticket, Plus, Loader2, Power } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, auth } from "../lib/api";
import { PLAN_LABELS } from "../lib/plans";

const TYPE_LABELS = { extra_trips: "Viajes extra", discount_percent: "% de descuento", free_months: "Meses gratis" };

export default function MasterPromoCodes() {
  const navigate = useNavigate();
  const user = auth.current();
  const [codes, setCodes] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [form, setForm] = useState({ code: "", type: "extra_trips", value: "", note: "", max_redemptions: "", validity: "", applicablePlans: [] });
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");

  const load = () => {
    api.get("/master/promo-codes").then(({ data }) => { setCodes(data.codes); setRedemptions(data.redemptions); }).catch(() => {});
  };

  useEffect(() => {
    if (!user || user.role !== "master") { navigate("/admin/login"); return; }
    load();
  }, []);

  if (!user) return null;

  const togglePlan = (plan) => {
    setForm((f) => ({
      ...f,
      applicablePlans: f.applicablePlans.includes(plan)
        ? f.applicablePlans.filter((p) => p !== plan)
        : [...f.applicablePlans, plan],
    }));
  };

  const create = async (e) => {
    e.preventDefault();
    setErr("");
    setCreating(true);
    try {
      const unit = form.validity.slice(-1); // "d" o "m"
      const amount = form.validity ? parseInt(form.validity, 10) : null;
      await api.post("/master/promo-codes", {
        code: form.code, type: form.type, value: parseFloat(form.value),
        note: form.note, max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions, 10) : null,
        valid_days: unit === "d" ? amount : null,
        valid_months: unit === "m" ? amount : null,
        applicable_plans: form.applicablePlans.length ? form.applicablePlans : null,
      });
      setForm({ code: "", type: "extra_trips", value: "", note: "", max_redemptions: "", validity: "", applicablePlans: [] });
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo crear el código");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (code) => {
    try {
      await api.patch(`/master/promo-codes/${code}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el código");
    }
  };

  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-12 space-y-10">
        <div className="fade-up">
          <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-3">Master · Tulkas</p>
          <h1 className="font-display text-5xl font-light tracking-tight">
            Códigos <span className="text-[var(--brand-cyan,#FF85AC)]">Promocionales</span>
          </h1>
          <p className="text-neutral-400 mt-3 max-w-2xl">
            Invitación, viajes extra gratis, descuentos — cada agencia solo puede usar un código una vez.
          </p>
        </div>

        <article className="glass rounded-3xl p-8 border border-white/10">
          <div className="flex items-center gap-3 mb-6">
            <Ticket className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Crear código</h2>
          </div>
          {err && <p className="text-sm text-[#FF3B30] mb-4">{err}</p>}
          <form onSubmit={create} className="grid sm:grid-cols-2 gap-4">
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="CÓDIGO (ej. BIENVENIDA10)" required data-testid="new-code-input"
              className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white font-mono" />
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
              data-testid="new-code-type"
              className="bg-black border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white">
              {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
              type="number" placeholder="Valor (ej. 20 viajes, o 15%)" required data-testid="new-code-value"
              className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
            <input value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
              type="number" placeholder="Máx. usos (opcional)" data-testid="new-code-max"
              className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
            <select value={form.validity} onChange={(e) => setForm({ ...form, validity: e.target.value })}
              data-testid="new-code-validity"
              className="bg-black border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white">
              <option value="">Sin vencimiento</option>
              <option value="5d">Vence en 5 días</option>
              <option value="10d">Vence en 10 días</option>
              <option value="15d">Vence en 15 días</option>
              <option value="20d">Vence en 20 días</option>
              <option value="1m">Vence en 1 mes</option>
              <option value="2m">Vence en 2 meses</option>
              <option value="3m">Vence en 3 meses</option>
              <option value="6m">Vence en 6 meses</option>
              <option value="12m">Vence en 12 meses</option>
            </select>
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Nota para ti (opcional)" data-testid="new-code-note"
              className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white sm:col-span-2" />
            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">
                Aplica a (vacío = cualquier plan)
              </label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(PLAN_LABELS).map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => togglePlan(v)}
                    data-testid={`new-code-plan-${v}`}
                    className={`text-xs px-3.5 py-2 rounded-full border ${
                      form.applicablePlans.includes(v)
                        ? "border-[var(--brand-cyan,#FF85AC)] text-[var(--brand-cyan,#FF85AC)] bg-[var(--brand-cyan,#FF85AC)]/10"
                        : "border-white/15 text-neutral-400"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <button type="submit" disabled={creating} data-testid="create-code-btn"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold bg-[var(--brand-cyan,#FF85AC)] text-black hover:bg-[#FF9DC0] disabled:opacity-50">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Crear código
              </button>
            </div>
          </form>
        </article>

        <div>
          <h2 className="font-display text-xl mb-4">Códigos ({codes.length})</h2>
          <div className="space-y-3" data-testid="promo-codes-list">
            {codes.map((c) => {
              const expired = c.expires_at && new Date(c.expires_at) < new Date();
              return (
                <div key={c.code} className="glass rounded-2xl p-4 border border-white/10 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="font-mono text-white">{c.code} <span className="text-neutral-500 text-sm">· {TYPE_LABELS[c.type]} · {c.value}</span></p>
                    {c.note && <p className="text-xs text-neutral-600">{c.note}</p>}
                    <p className="text-xs text-neutral-500 mt-1">
                      {c.redemptions_count} usos{c.max_redemptions ? ` de ${c.max_redemptions}` : ""}
                      {c.expires_at && ` · vence ${new Date(c.expires_at).toLocaleDateString()}`}
                      {c.applicable_plans?.length ? ` · solo ${c.applicable_plans.map((p) => PLAN_LABELS[p]).join(", ")}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {expired && (
                      <span className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border text-[#FF3B30] border-[#FF3B30]/30">
                        Vencido
                      </span>
                    )}
                    <span className={`text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${
                      c.active ? "text-[var(--brand-cyan,#FF85AC)] border-[var(--brand-cyan,#FF85AC)]/30" : "text-neutral-500 border-white/10"
                    }`}>{c.active ? "Activo" : "Inactivo"}</span>
                    <button onClick={() => toggle(c.code)} data-testid={`toggle-code-${c.code}`} className="text-neutral-400 hover:text-[var(--brand-cyan,#FF85AC)] p-1.5">
                      <Power className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
            {codes.length === 0 && <p className="text-sm text-neutral-500">Sin códigos todavía.</p>}
          </div>
        </div>

        {redemptions.length > 0 && (
          <div>
            <h2 className="font-display text-xl mb-4">Canjes ({redemptions.length})</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="redemptions-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-neutral-500 border-b border-white/10">
                    <th className="py-3 pr-4">Agencia</th>
                    <th className="py-3 pr-4">Código</th>
                    <th className="py-3 pr-4">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map((r) => (
                    <tr key={r.id} className="border-b border-white/5">
                      <td className="py-3 pr-4 text-white">{r.agency_name}</td>
                      <td className="py-3 pr-4 font-mono text-[var(--brand-cyan,#FF85AC)]">{r.code}</td>
                      <td className="py-3 pr-4 text-neutral-500">{r.redeemed_at}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
}
